import cs from 'classnames';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { AssistantAvatar } from '@wix/ai-scheduling-assistant-view-react';
import { getChatWidgets, type OnWidgetAction } from '@wix/chat-widget-common-widgets';
import { ChatMessageData, ChatWidget } from '@wix/chat-widget-ui-lib';
import { BounceAnimation, Box } from '@wix/design-system';
import { useHttpClientsContext } from '../../../context/HttpClientsContext';
import { useChatAnchorClickEvent, useChatScrollEvents } from '../../../hooks/useChatEvents';
import { ChatSearchParam, useChatSearchParams } from '../../../hooks/useChatSearchParams';
import { useScreenWidth } from '../../../hooks/useScreenWidth';
import { useUouLanguage } from '../../../hooks/useUouLanguage';
import { MessageType, usePostMessageListener } from '../../../hooks/useWindowPostMessage';
import { useUoUBusinessData } from '../hooks/useUoUBusinessData';
import { useUoUChatService } from '../hooks/useUoUChatService';
import { useUoUChatUI } from '../hooks/useUoUChatUI';
import { WidgetCommonContext } from '../types';
import { getSchedulingChatWidgets } from '../widget';
import { ChatLoader } from './chat-loader/ChatLoader';
import { UoUChatFooter } from './footer/UoUChatFooter';
import { UoUChatHeader } from './header/UoUChatHeader';
import { RiveBackground } from './rive/RiveBackground';
import { RiveSplashScreen } from './rive/RiveTextSplash';
import s from './uouChat.scss';

type UoUChatProps = {
  useIndividualAssistant?: boolean;
};

export const UoUChat: FC<UoUChatProps> = ({ useIndividualAssistant = false }) => {
  const { chatHttpClient } = useHttpClientsContext();
  const language = useUouLanguage();
  const { hideFooter, viewMode, fromWaitlist, deleteSearchParam } = useChatSearchParams();
  const { isMobile } = useScreenWidth();

  const [isLoading, setIsLoading] = useState<boolean>(true);

  const { showSplash, showChat, showChatHandler, showDisclaimer, hideDisclaimer } = useUoUChatUI(useIndividualAssistant);

  const { imageUrl, businessName, assistantName, fetchBusinessProperties } = useUoUBusinessData();

  const {
    isLoading: isChatLoading,
    messages,
    tags,
    sendUserMessage,
    loadOrCreateConversation,
    onRetry,
    refetchConnectionStatus,
  } = useUoUChatService(useIndividualAssistant, hideDisclaimer);

  useChatScrollEvents();
  useChatAnchorClickEvent();
  usePostMessageListener(MessageType.PAYMENT_SUCCESS, () => {
    sendUserMessage({ content: language.ASA_STANDALONE_CHAT_PAYMENT_SUCCESS_MESSAGE(), role: 'user' });
  });

  const chatMessages = useMemo((): ChatMessageData[] => {
    if (!messages.length || viewMode) {
      return messages;
    }

    const copyMessages = messages.map((message) => {
      return message;
    });

    const lastMessage = copyMessages[copyMessages.length - 1];
    if (lastMessage.content === 'How can I assist you?') {
      return [...copyMessages, { content: language.ASA_STANDALONE_CHAT_NOW_DIVIDER(), role: 'divider' }, lastMessage];
    }

    return copyMessages;
  }, [language, messages, viewMode]);

  const onWidgetAction: OnWidgetAction = async (widgetName, data) => {
    if (data.action === 'error') {
      console.log('onWidgetAction:Error', widgetName, data);
    }
  };

  const widgetContext: WidgetCommonContext = {
    send: async (content: string): Promise<void> => {
      sendUserMessage({ content, role: 'user' });
    },
    onWidgetAction,
    onRetry,
    refetchConnectionStatus,
    httpClient: chatHttpClient,
    assistantName,
    emitSideEffect: () => Promise.resolve(),
    t: () => '',
  };

  const widgets = [...getChatWidgets(widgetContext), ...getSchedulingChatWidgets(widgetContext)];
  // @ts-expect-error - TODO: platform should fix this
  widgets.context = widgetContext;

  const init = useCallback(async () => {
    setIsLoading(true);
    await Promise.all([loadOrCreateConversation(), fetchBusinessProperties()]);
    setIsLoading(false);
  }, [fetchBusinessProperties, loadOrCreateConversation]);

  const sendAndDeleteUserMessage = useCallback(async () => {
    if (fromWaitlist) {
      await sendUserMessage({ content: `<HIDDEN>${language.ASA_STANDALONE_CHAT_WAITLIST_USER_MESSAGE()}</HIDDEN>`, role: 'user' });
      deleteSearchParam(ChatSearchParam.FromWaitlist);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromWaitlist, sendUserMessage, deleteSearchParam]);

  useEffect(() => {
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isLoading && fromWaitlist) {
      sendAndDeleteUserMessage();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, fromWaitlist]);

  return isLoading ? (
    <ChatLoader />
  ) : showSplash ? (
    <div className={s.riveBackgroundContainer}>
      <RiveSplashScreen assistantName={assistantName} businessName={businessName} onEnd={showChatHandler} />
    </div>
  ) : (
    <Box className={s.chatWidgetContainer} direction="vertical" align="center" gap={isMobile ? 1 : 4}>
      {!viewMode && <UoUChatHeader businessName={businessName} />}
      {showChat ? (
        <div className={cs(s.chatChildrenContainer, { [s.chatChildrenContainerViewMode]: viewMode })}>
          <ChatWidget
            isInputVisible={!viewMode}
            isLoading={isChatLoading}
            assistantName={assistantName}
            widgets={widgets}
            messageToSendPlaceholder={language.ASA_STANDALONE_CHAT_MESSAGE_TO_SEND_PLACEHOLDER()}
            onWidgetAction={onWidgetAction}
            onSendMessage={sendUserMessage}
            assistantAvatar={({ isLoading: isCurrentMessageLoading }) => (
              <>
                <BounceAnimation active={isCurrentMessageLoading} loop>
                  <AssistantAvatar assistantName={assistantName} imageUrl={imageUrl} size={18} />
                </BounceAnimation>
              </>
            )}
            theme={{ name: 'scheduling-assistant' }}
            chatMessages={chatMessages}
            tags={tags}
            onError={(error) => {
              console.error('error on chat widget', error);
            }}
          />
        </div>
      ) : (
        <Box flex={1} />
      )}
      {hideFooter || viewMode ? <Box paddingBottom={5} /> : <UoUChatFooter showDisclaimer={showDisclaimer} />}
      <div className={cs(s.riveBackgroundContainer, { [s.riveBackgroundContainerFadeOut]: !showSplash })}>
        <RiveBackground />
      </div>
    </Box>
  );
};
